import React, { useEffect, useMemo } from "react";
import { Autocomplete, Button, Modal, Typography } from "@mui/material";
import { useForm } from "react-hook-form";

import Input from "components/Input";

// import { useAppDispatch } from "store/hooks";
// import { handleCreateWorkingAreaRequest } from "store/modules/Logistics/thunk.store";
// import { useAuth } from "context/AuthContext";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleAddCatalogProductsRequest } from "store/modules/Catalog/thunk.store";
import { useTranslation } from "react-i18next";
import * as Styled from "./styles";

interface AddNewProductData {
  name: string;
  description: string;
  categoryName: string;
  categoryId: string;
  brandName: string;
  brandId: string;
}

interface AddNewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const AddNewProductModal: React.FC<AddNewProductModalProps> = ({
  isOpen,
  title,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<AddNewProductData>();

  const { catalogCategories, catalogBrands } = useAppSelector(
    (state) => state.catalog,
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const handleSubmitForm = async (data: AddNewProductData) => {
    await dispatch(
      handleAddCatalogProductsRequest({
        data: {
          name: data.name,
          description: data.description,
          categoryId: data.categoryId,
          brandId: data.brandId,
        },
      }),
    ).unwrap();
    onClose();
  };

  const IBrands = useMemo(() => {
    return catalogBrands?.data?.map((x) => ({
      label: x.name,
      id: x.id,
    }));
  }, [catalogBrands]);

  const ICategories = useMemo(() => {
    return catalogCategories?.data?.map((x) => ({
      label: x.name,
      id: x.id,
    }));
  }, [catalogCategories]);

  useEffect(() => {
    reset();
  }, [isOpen, reset]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Styled.Content>
        <Typography variant="h6" mb={2}>
          {`${t("add")} ${title}`}
        </Typography>

        <Styled.TypeLinkContainer onSubmit={handleSubmit(handleSubmitForm)}>
          <Input
            name="name"
            error={errors.name}
            label={`${t("title")} ${t("of")} ${title}`}
            register={register}
            fullWidth
            registerOptions={{
              required: t("requiredField"),
            }}
            sx={{ mb: 2 }}
          />

          <Input
            name="description"
            error={errors.description}
            label={t("description")}
            register={register}
            fullWidth
            registerOptions={{
              required: t("requiredField"),
            }}
            sx={{ mb: 2 }}
          />
          <Styled.ContainerType>
            <Autocomplete
              style={{ width: "50%" }}
              disablePortal
              id="combo-box-demo"
              options={ICategories || []}
              size="small"
              onChange={(_, e) => setValue("categoryId", e?.id as string)}
              sx={{
                mb: 2,
                width: 200,
                "& input": { width: 200 },
              }}
              renderInput={(params) => (
                <Input
                  {...params}
                  name="categoryName"
                  error={errors.categoryId}
                  label={t("category")}
                  register={register}
                  fullWidth
                  registerOptions={{
                    required: t("requiredField"),
                  }}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Autocomplete
              style={{ width: "50%" }}
              disablePortal
              id="combo-box-demo"
              options={IBrands || []}
              size="small"
              onChange={(_, e) => setValue("brandId", e?.id as string)}
              sx={{
                mb: 2,
                width: 200,
                "& input": { width: 200 },
              }}
              renderInput={(params) => (
                <Input
                  {...params}
                  name="brandName"
                  error={errors.brandId}
                  register={register}
                  fullWidth
                  label={t("brand")}
                  sx={{ mb: 2 }}
                />
              )}
            />
          </Styled.ContainerType>

          <Styled.ActionsContainer>
            <Button
              color="inherit"
              variant="text"
              onClick={onClose}
              endIcon={<CloseIcon />}
            >
              {t("close")}
            </Button>
            <Button
              color="secondary"
              variant="text"
              type="submit"
              endIcon={<SaveIcon />}
            >
              {t("save")}
            </Button>
          </Styled.ActionsContainer>
        </Styled.TypeLinkContainer>
      </Styled.Content>
    </Modal>
  );
};

export default AddNewProductModal;
